import { useId, useState } from "react";
import { Select } from "antd";
import {
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { usePlotOutline } from "../../hooks/usePlotOutline";
import { type SeedAssignmentErrors } from "../../utils/seedValidation";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import SeedRateInput, { SEED_RATE_CONTROLS } from "../SeedRateInput";
import { getSeedProductColor } from "../../utils/seedProductColors";
import "./style.less";

export interface AssignmentGroupRow {
  id: string;
  group: string;
  plot: number;
  productId: string;
  seedRate: number | null;
}

export interface AssignmentProductOption {
  label: string;
  value: string;
  rateOptions?: number[];
  color?: string;
  range?: { min: number; max: number };
}

interface AssignmentGroupsProps {
  rows: AssignmentGroupRow[];
  productOptions: AssignmentProductOption[];
  errors?: Record<string, SeedAssignmentErrors>;
  unit?: string;
  onAssignmentChange: (
    id: string,
    update: Partial<Pick<AssignmentGroupRow, "productId" | "seedRate">>
  ) => void;
  onShowPlot?: (id: string) => void;
}

const AssignmentGroups = ({
  rows,
  productOptions,
  errors = {},
  unit = "s/ha",
  onAssignmentChange,
  onShowPlot,
}: AssignmentGroupsProps): JSX.Element => {
  const text = useSeedTranslations();
  const instanceId = useId();
  const { isOutlined, toggleOutline } = usePlotOutline();

  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const [focusedPlot, setFocusedPlot] = useState<string | null>(null);

  const groups = Array.from(new Set(rows.map((row) => row.group)));

  const toggleGroup = (group: string): void => {
    setCollapsedGroups((previous) =>
      previous.includes(group)
        ? previous.filter((item) => item !== group)
        : [...previous, group]
    );
  };

  const handleShowPlot = (id: string): void => {
    toggleOutline(id);
    setFocusedPlot((previous) => (previous === id ? null : id));
    onShowPlot?.(id);
  };

  return (
    <div className="rd-assignment-groups" data-testid="assignment-groups">
      {groups.map((group) => {
        const expanded = !collapsedGroups.includes(group);

        return (
          <section className="rd-assignment-groups__group" key={group}>
            <button
              type="button"
              className="rd-assignment-groups__group-toggle"
              aria-expanded={expanded}
              onClick={() => {
                toggleGroup(group);
              }}
            >
              <DownOutlined
                className={expanded ? "" : "is-collapsed"}
                aria-hidden
              />
              {text("groupLabel", { group })}
            </button>

            {expanded && (
              <div className="rd-assignment-groups__table-wrap">
                <table className="rd-assignment-groups__table">
                  <thead>
                    <tr>
                      <th scope="col">{text("plots")}</th>
                      <th scope="col">{text("product")}</th>
                      <th scope="col">{text("seedRateLabel", { unit })}</th>
                      <th scope="col">
                        <span className="rd-assignment-groups__sr-only">
                          {text("showPlot")}
                        </span>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows
                      .filter((row) => row.group === group)
                      .map((row) => {
                        const productIndex = productOptions.findIndex(
                          (option) => option.value === row.productId
                        );
                        const selectedProduct = productOptions[productIndex];
                        const colorProducts = productOptions.map((option) => ({
                          id: option.value,
                          name: option.label,
                          options: option.rateOptions,
                          range: option.range,
                        }));
                        const rowColor =
                          selectedProduct === undefined
                            ? "#83cafa"
                            : getSeedProductColor(
                                colorProducts[productIndex],
                                colorProducts,
                                row.seedRate,
                                rows
                                  .filter(
                                    (item) =>
                                      item.productId === row.productId &&
                                      item.group === row.group
                                  )
                                  .map((item) => item.seedRate)
                              );

                        const error = errors[row.id];
                        const productErrorId = `${instanceId}-${row.id}-product-error`;
                        const rateErrorId = `${instanceId}-${row.id}-rate-error`;

                        return (
                          <tr
                            key={row.id}
                            className={`rd-assignment-groups__plot-row ${
                              focusedPlot === row.id ? "is-focused" : ""
                            }`}
                            style={{
                              borderLeftColor: rowColor,
                            }}
                          >
                            <th scope="row">{row.plot}</th>

                            <td>
                              <Select<string>
                                aria-label={text("plotProductAria", {
                                  group,
                                  plot: row.plot,
                                })}
                                value={
                                  row.productId === ""
                                    ? undefined
                                    : row.productId
                                }
                                options={productOptions}
                                status={error?.product ? "error" : undefined}
                                aria-invalid={Boolean(error?.product)}
                                aria-describedby={
                                  error?.product ? productErrorId : undefined
                                }
                                onChange={(value) => {
                                  onAssignmentChange(row.id, {
                                    productId: value,
                                  });
                                }}
                              />

                              {error?.product && (
                                <span
                                  id={productErrorId}
                                  className="rd-assignment-groups__error"
                                  role="alert"
                                >
                                  {error.product}
                                </span>
                              )}
                            </td>

                            <td>
                              <SeedRateInput
                                controls={SEED_RATE_CONTROLS}
                                options={
                                  productOptions[productIndex]?.rateOptions
                                }
                                aria-label={text("plotRateAria", {
                                  group,
                                  plot: row.plot,
                                })}
                                value={row.seedRate}
                                status={error?.rate ? "error" : undefined}
                                aria-invalid={Boolean(error?.rate)}
                                aria-describedby={
                                  error?.rate ? rateErrorId : undefined
                                }
                                onChange={(value) => {
                                  onAssignmentChange(row.id, {
                                    seedRate: value,
                                  });
                                }}
                              />

                              {error?.rate && (
                                <span
                                  id={rateErrorId}
                                  className="rd-assignment-groups__error"
                                  role="alert"
                                >
                                  {error.rate}
                                </span>
                              )}
                            </td>

                            <td>
                              <button
                                type="button"
                                className="rd-assignment-groups__view-plot"
                                aria-label={text("highlightPlotAria", {
                                  group,
                                  plot: row.plot,
                                })}
                                aria-pressed={isOutlined(row.id)}
                                onClick={() => {
                                  handleShowPlot(row.id);
                                }}
                              >
                                {isOutlined(row.id) ? (
                                  <EyeOutlined aria-hidden />
                                ) : (
                                  <EyeInvisibleOutlined aria-hidden />
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default AssignmentGroups;
